import { SignIn, useAuth } from "@mattfinkel/common-web";
import type { ReactNode } from "react";
import { Route, Routes } from "react-router";
import { Layout } from "./components/Layout";
import { auth, googleProvider } from "./firebase";
import Browse from "./pages/Browse";
import NotFound from "./pages/NotFound";
import Audit from "./pages/Audit";
import Dismissed from "./pages/Dismissed";
import Patterns from "./pages/Patterns";
import Saved from "./pages/Saved";

/** The centred card the app shows before it has an authorized session. */
function Gate({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <header className="flex items-center gap-4">
          <img src="/mark.svg" alt="" className="h-16 w-16 rounded-2xl shadow-xl shadow-black/30" />
          <div>
            <h1 className="merch-gate-title text-2xl">Band Merch</h1>
            <p className="mt-1 text-sm text-neutral-400">Band merch finder</p>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const { state, signOut, retry } = useAuth();

  // Routes are mounted ONLY inside the allowed branch, so no page component
  // can render for an unauthorized session even if someone deep-links to it.
  // The backend enforces the same allowlist on every request regardless -- the
  // UI gate is not the boundary -- but this keeps the two consistent.
  if (state.status === "allowed") {
    return (
      <Routes>
        <Route element={<Layout email={state.user.email} onSignOut={signOut} />}>
          <Route index element={<Browse />} />
          <Route path="saved" element={<Saved />} />
          <Route path="dismissed" element={<Dismissed />} />
          <Route path="patterns" element={<Patterns />} />
          <Route path="audit" element={<Audit />} />
          {/* Hosting's SPA catch-all serves index.html for any unknown path,
              so an unmatched deep link arrives here rather than as a CDN 404. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Gate>
      {state.status === "idle" && <SignIn auth={auth} provider={googleProvider} />}

      {state.status === "verifying" && (
        <div className="rounded-md border border-neutral-800 bg-neutral-900 p-4 text-sm">
          Verifying access…
        </div>
      )}

      {/* "Could not ask" is NOT "denied", and collapsing the two is the bug
          this state exists to prevent: a scale-to-zero cold start or a
          transient 5xx would otherwise sign a legitimate user out and tell
          them they are not allowed. They keep their session and get a retry. */}
      {state.status === "unavailable" && (
        <div className="space-y-3">
          <div className="rounded-md border border-amber-900 bg-amber-950/40 p-4 text-sm">
            Could not reach the server: {state.reason}
          </div>
          <button
            onClick={retry}
            className="rounded-md bg-neutral-100 px-4 py-2 text-neutral-900 hover:bg-white"
          >
            Try again
          </button>
        </div>
      )}

      {state.status === "denied" && (
        <div className="space-y-3">
          <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
            Access denied: {state.reason}
          </div>
          <SignIn auth={auth} provider={googleProvider} />
        </div>
      )}
    </Gate>
  );
}
